import React, { useEffect, useState, useCallback, useRef } from 'react';
import compose from 'helpers/compose';
import { observer, inject } from 'mobx-react';
import { useTranslation, getI18n } from 'react-i18next';

import { Paper } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';

import Message, { MessageVariant } from 'components/Message/Message';

import { UserStoreKC, ViewKeys } from 'stores/userStoreKC';
import { ServiceMessage, ServicesResponse, ServiceStatus, services } from 'stores/uiStore';

import { RootStore } from 'stores/rootStore';
import { API_NOTIFICATIONS, API_SERVICES } from 'api/endpoints';
import { APP_CONFIG } from 'appConfig';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      paddingX: theme.spacing(2),
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      zIndex: 1,
    },
    flexWrapper: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
);

//TODO: notifications store ?

interface Props {
  userStore?: UserStoreKC;
  rootStore?: RootStore;
}

export interface MessageData {
  title?: string;
  message: string;
  severity: 'NORMAL' | 'CRITICAL';
  realms?: string[];
  id?: string;
  visible: boolean;
  validFrom: string;
  validTo: string;
}

const Notification: React.FC<Props> = ({ userStore, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [messages, setMessages] = useState<MessageData[] | undefined>(undefined);
  const [serviceMessages, setServiceMessages] = useState<ServiceMessage[] | undefined>(undefined);
  const timerID = useRef(0);

  const checkMessages = useCallback(async () => {
    if (rootStore) {
      const prevState: ServicesResponse = JSON.parse(sessionStorage.getItem('services') || '{}');

      const response1 = await rootStore.stores.apiStore.get<ServicesResponse>(API_SERVICES, true);
      const servicesInStorage: ServicesResponse = {};
      if (response1?.data) {
        const status = Object.entries(response1.data)
          .map(([key, value]) => {
            servicesInStorage[key] = {
              status:
                // user didn't close the "Back to OK" message
                value.status === ServiceStatus.OK &&
                prevState[key]?.status === ServiceStatus.DOWN &&
                !userStore!.inViewSetting(ViewKeys.HIDE_MESSAGES, `${key}_OK`)
                  ? ServiceStatus.DOWN
                  : value.status,
            };
            // next "Back to OK" message won't be hidden
            if (value.status === ServiceStatus.DOWN) {
              userStore!.removeFromViewSetting(ViewKeys.HIDE_MESSAGES, `${key}_OK`);
            }
            return { id: key, status: value.status };
          })
          .filter(
            (msg) => services[msg.id] !== undefined && services[msg.id].realms.includes(userStore!.loggedUser!.realm),
          );
        setServiceMessages(
          status.filter(
            (msg) =>
              msg.status === ServiceStatus.DOWN ||
              (Object.keys(prevState).length && prevState[msg.id]?.status !== msg.status),
          ),
        );
        rootStore.stores.uiStore.setServiceMessages(status);
        sessionStorage.setItem('services', JSON.stringify(servicesInStorage));
      }

      const response = await rootStore.stores.apiStore.get<MessageData[]>(API_NOTIFICATIONS);
      if (response?.data) {
        setMessages(
          response.data.filter(
            (msg) =>
              !msg.realms ||
              !msg.realms.length ||
              msg.realms.map((r) => r.toUpperCase()).includes(userStore!.loggedUser!.realm.toUpperCase()),
          ),
        );
      }
      timerID.current = window.setTimeout(checkMessages, APP_CONFIG.messageInterval);
    }
  }, [userStore, rootStore]);

  useEffect(() => {
    checkMessages();
    return function cleanup() {
      window.clearTimeout(timerID.current);
    };
  }, [timerID, checkMessages]);

  return (
    <Paper elevation={1} className={classes.root} square>
      <div className={classes.flexWrapper}>
        {serviceMessages && // conditions needed so the output isn't "0"
          serviceMessages.length > 0 &&
          serviceMessages.map((msg) => (
            <Message
              key={`service_${msg.id}`}
              id={`service_${msg.id}_${msg.status}`}
              // show the message the next time it's DOWN/OK, if it went back to OK
              reenableIdOnClose={`service_${msg.id}_${
                msg.status === ServiceStatus.OK ? ServiceStatus.DOWN : ServiceStatus.OK
              }`}
              title={`${t('service_' + msg.status)}: ${t('service_' + msg.id)} `}
              message={
                msg.status === ServiceStatus.DOWN && getI18n().exists(`service_${msg.id}_DOWN`)
                  ? t(`service_${msg.id}_DOWN`)
                  : undefined
              }
              variant={msg.status === ServiceStatus.OK ? MessageVariant.SUCCESS : MessageVariant.ERROR}
              userStore={userStore}
              stack
            />
          ))}

        {messages && // conditions needed so the output isn't "0"
          messages.length > 0 &&
          messages.map((msg) => (
            <Message
              key={`notification_${msg.id}`}
              id={`notification_${msg.id}`}
              title={msg.title}
              message={msg.message}
              variant={msg.severity === 'NORMAL' ? MessageVariant.INFO : MessageVariant.ERROR}
              userStore={userStore}
              stack
            />
          ))}
      </div>
    </Paper>
  );
};

export default compose(inject('userStore', 'rootStore'), observer)(Notification);
